import * as superagent from 'superagent';
import { useCallback } from 'react';
import {
    RequestStatus,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks';
import { Store } from 'dg-web-shared/lib/Flux';
import * as CommonQueryActions from '../../../common/actions/CommonQueryActions';
import {
    navigateToPermitPurchaseConfirmation,
    refreshStateSlicesOnSuccessfulPermitPurchase,
} from '../../../park-create/actions/PermitActions';
import {
    PairingState,
    PendingTwintPairing,
    transactionFromUoFState,
    TwintUoFStateResponse,
} from 'dg-web-shared/common/utils/TwintPairing';
import {
    TwintParkingpayPaymentStates,
    TwintParkingpayPendingState,
    TwintParkingpayUofStates,
    UiPaymentOrigin,
} from 'dg-web-shared/common/utils/TwintPairingParkingpay';
import { useServerFetch } from '../../../hooks/ServerStateHooks';
import { DEVICE_UUID } from '../../../utils/ActionLog.ts';

export const api = `/ui-api/customer-account/twint`;

export function useRequests(
    mode: PendingTwintPairing.TwintPaymentType,
    requestCounter: number,
    token: string | null,
    paymentAttemptId: number | null,
): TwintParkingpayPendingState {
    const [requestState] = useServerFetch<
        TwintTransacitonStateResponseTypes | TwintUofStateResponseTypes,
        {
            token: string | null;
            paymentAttemptId: number | null;
            count: number;
            mode: PendingTwintPairing.TwintPaymentType;
        }
    >(
        ({ token, paymentAttemptId, mode }) => {
            if (mode === PendingTwintPairing.TwintPaymentType.UOF) {
                return {
                    url: `${api}-alias/state?pairingToken=${token}&paymentAttemptId=${paymentAttemptId}`,
                };
            }

            return {
                url: `${api}/transaction-state?pairingToken=${token}&paymentAttemptId=${paymentAttemptId}&mode=${mode}&deviceUUID=${DEVICE_UUID}`,
            };
        },
        {
            token,
            paymentAttemptId,
            count: requestCounter,
            mode,
        },
    );

    if (requestState.status === RequestStatus.ERROR || !requestState.data) {
        if (mode === PendingTwintPairing.TwintPaymentType.UOF) {
            return {
                mode: PendingTwintPairing.TwintPaymentType.UOF,
                state: PairingState.UNKNOWN,
                requestState: requestState.status,
            };
        }

        return {
            mode,
            state: PairingState.UNKNOWN,
            requestState: requestState.status,
        };
    }

    if (mode === PendingTwintPairing.TwintPaymentType.UOF) {
        return mapUofResponse(
            requestState.data as TwintUofStateResponseTypes,
            requestState.status,
        );
    }

    return mapPaymentResponse(
        requestState.data as TwintTransacitonStateResponseTypes,
        requestState.status,
        mode,
    );
}

type TwintUofStateResponseTypes =
    | TwintUofStateResponseBase
    | TwintTicketUofStateResponse;

interface TwintParkingpayUofStateResponse extends TwintUoFStateResponse {
    uiPaymentOrigin: UiPaymentOrigin;
}

interface TwintUofStateResponseBase extends TwintParkingpayUofStateResponse {
    uiPaymentOrigin:
        | UiPaymentOrigin.STORE_ALIAS_FROM_CHECKIN
        | UiPaymentOrigin.STORE_ALIAS_FROM_ACCOUNT;
}

interface TwintTicketUofStateResponse extends TwintParkingpayUofStateResponse {
    uiPaymentOrigin: UiPaymentOrigin.STORE_ALIAS_FROM_TICKET;
    ticketId: string;
}

enum TwintTransactionState {
    UNKNOWN = 'UNKNOWN',
    CREATED = 'CREATED',
    AUTHORIZED = 'AUTHORIZED',
    SETTLED = 'SETTLED',
    ABORTED = 'ABORTED',
}

type TwintTransacitonStateResponseTypes =
    | TwintTransactionStateResponseBase
    | TwintPermitTransactionStateResponse
    | TwintTicketTransactionStateResponse;

interface TwintTransactionStateResponse {
    state: TwintTransactionState;
    token: number;
    amount: string;
    uiPaymentOrigin: UiPaymentOrigin;
}

interface TwintTransactionStateResponseBase extends TwintTransactionStateResponse {
    uiPaymentOrigin:
        | UiPaymentOrigin.TOPUP_FROM_ACCOUNT
        | UiPaymentOrigin.TOPUP_FROM_CHECKIN;
}

interface TwintPermitTransactionStateResponse extends TwintTransactionStateResponse {
    uiPaymentOrigin: UiPaymentOrigin.TOPUP_FROM_PERMIT;
    contractExternalId: string;
}

interface TwintTicketTransactionStateResponse extends TwintTransactionStateResponse {
    uiPaymentOrigin: UiPaymentOrigin.TOPUP_FROM_TICKET;
    ticketId: string;
}

function mapPaymentResponse(
    data: TwintTransacitonStateResponseTypes,
    status: RequestStatus,
    mode:
        | PendingTwintPairing.TwintPaymentType.ORDER
        | PendingTwintPairing.TwintPaymentType.COMBINED_UOF_OPTIONAL
        | PendingTwintPairing.TwintPaymentType.COMBINED_UOF_REQUIRED,
): TwintParkingpayPaymentStates {
    switch (data.uiPaymentOrigin) {
        case UiPaymentOrigin.TOPUP_FROM_PERMIT:
            return {
                mode: mode,
                state: pairingStateFromTransactionState(data.state),
                requestState: status,
                amount: data.amount,
                uiPaymentOrigin: data.uiPaymentOrigin,
                contractExternalId: data.contractExternalId,
            };
        case UiPaymentOrigin.TOPUP_FROM_ACCOUNT:
        case UiPaymentOrigin.TOPUP_FROM_CHECKIN:
            return {
                mode: mode,
                state: pairingStateFromTransactionState(data.state),
                requestState: status,
                amount: data.amount,
                uiPaymentOrigin: data.uiPaymentOrigin,
            };
        case UiPaymentOrigin.TOPUP_FROM_TICKET:
            return {
                mode: mode,
                state: pairingStateFromTransactionState(data.state),
                requestState: status,
                amount: data.amount,
                uiPaymentOrigin: data.uiPaymentOrigin,
                ticketId: data.ticketId,
            };
        default:
            throw new Error(
                'Invalid topup payment origin ' +
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (data as any)?.uiPaymentOrigin,
            );
    }
}

function mapUofResponse(
    data: TwintUofStateResponseTypes,
    status: RequestStatus,
): TwintParkingpayUofStates {
    switch (data.uiPaymentOrigin) {
        case UiPaymentOrigin.STORE_ALIAS_FROM_TICKET: {
            return {
                mode: PendingTwintPairing.TwintPaymentType.UOF,
                state: transactionFromUoFState(data.state),
                requestState: status,
                uiPaymentOrigin: data.uiPaymentOrigin,
                ticketId: data.ticketId,
            };
        }

        case UiPaymentOrigin.STORE_ALIAS_FROM_ACCOUNT:
        case UiPaymentOrigin.STORE_ALIAS_FROM_CHECKIN:
            return {
                mode: PendingTwintPairing.TwintPaymentType.UOF,
                state: transactionFromUoFState(data.state),
                requestState: status,
                uiPaymentOrigin: data.uiPaymentOrigin,
            };
        default:
            throw new Error(
                'Invalid store alias payment origin ' +
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (data as any)?.uiPaymentOrigin,
            );
    }
}

const pairingStateFromTransactionState = (state: TwintTransactionState) => {
    switch (state) {
        case TwintTransactionState.ABORTED:
            return PairingState.ABORTED;
        case TwintTransactionState.UNKNOWN:
            return PairingState.UNKNOWN;
        case TwintTransactionState.CREATED:
            return PairingState.CREATED;
        case TwintTransactionState.AUTHORIZED:
            return PairingState.CONFIRMED_BY_USER;
        case TwintTransactionState.SETTLED:
            return PairingState.SUCCESS;
    }
};

export function useAbortTwintTransaction(
    mode: PendingTwintPairing.TwintPaymentType,
) {
    const [state, write] = useServerWrite<
        {
            pairingToken: string | null;
            paymentAttemptId: number | null;
            mode?: PendingTwintPairing.TwintPaymentType;
        },
        TwintUoFStateResponse | null
    >(() => ({
        url:
            mode === PendingTwintPairing.TwintPaymentType.UOF
                ? `${api}-alias/abort`
                : `${api}/abort-transaction`,
    }));

    const abort = useCallback(
        (pairingToken: string | null, paymentAttemptId: number | null) => {
            write(
                mode === PendingTwintPairing.TwintPaymentType.UOF
                    ? { pairingToken, paymentAttemptId }
                    : { pairingToken, paymentAttemptId, mode },
            );
        },
        [mode, write],
    );

    return [state, abort] as const;
}

export function settleTwint(
    store: Store,
    token: string | null,
    paymentAttemptId: number | null,
    refetchTransactionList: () => void,
    mode: PendingTwintPairing.TwintPaymentType,
) {
    superagent
        .post('/ui-api/customer-account/twint/settle-transaction')
        .send({
            pairingToken: token,
            paymentAttemptId: paymentAttemptId,
            mode: mode,
            deviceUUID: DEVICE_UUID,
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .end((_err: any, res: superagent.Response) => {
            // re-fetch the balance
            store.legacyUpdater(CommonQueryActions.receiveBalance);

            // navigate to new permit
            const contractExternalId = res?.body?.contractExternalId;
            if (contractExternalId) {
                refetchTransactionList();
                refreshStateSlicesOnSuccessfulPermitPurchase(store);
                navigateToPermitPurchaseConfirmation(
                    store,
                    contractExternalId,
                    true,
                );
            }
        });
}
